import React from 'react';
import PropTypes from 'prop-types';
import { Card } from './BaseComponents';

/**
 * StatCard Component
 * 
 * Statistics display card with large number and label.
 * Used in Dashboard, CourseView, etc.
 * 
 * Usage:
 * <StatCard value={42} label="Aktive Kurse" color="primary" />
 * <StatCard value={128} label="Teilnehmer" icon="👥" trend="+12%" />
 */
export const StatCard = ({
  value,
  label,
  color = 'primary', // 'primary', 'success', 'warning', 'danger', 'secondary'
  icon = null,
  trend = null,
  trendUp = true,
  onClick = null,
  className = ''
}) => {
  const colorClasses = {
    primary: 'text-primary-600 dark:text-primary-400',
    success: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    danger: 'text-danger-600 dark:text-danger-400',
    secondary: 'text-secondary-600 dark:text-secondary-400'
  };

  const trendColorClasses = trendUp 
    ? 'text-success-600 dark:text-success-400' 
    : 'text-danger-600 dark:text-danger-400';

  return (
    <Card 
      className={`text-center ${className}`}
      hover={!!onClick}
      onClick={onClick}
    >
      {/* Icon (optional) */}
      {icon && (
        <div className="text-3xl mb-2">{icon}</div>
      )}
      
      {/* Value */}
      <div className={`text-4xl font-bold ${colorClasses[color]}`}>
        {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
      </div>
      
      {/* Label */}
      <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {label}
      </div>
      
      {/* Trend (optional) */}
      {trend && (
        <div className={`text-xs mt-2 font-medium ${trendColorClasses}`}>
          {trendUp ? '↑' : '↓'} {trend}
        </div>
      )}
    </Card>
  );
};

StatCard.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'secondary']),
  icon: PropTypes.string,
  trend: PropTypes.string,
  trendUp: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string
};

/**
 * StatCardGrid Component
 * 
 * Responsive grid layout for StatCards
 * 
 * Usage:
 * <StatCardGrid>
 *   <StatCard value={42} label="Kurse" />
 *   <StatCard value={15} label="Vorlagen" />
 * </StatCardGrid>
 */
export const StatCardGrid = ({
  children,
  columns = 3, // 2, 3, 4
  className = ''
}) => {
  const gridClasses = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${gridClasses[columns]} gap-6 ${className}`}>
      {children}
    </div>
  );
};

StatCardGrid.propTypes = {
  children: PropTypes.node.isRequired,
  columns: PropTypes.oneOf([2, 3, 4]),
  className: PropTypes.string
};

export default StatCard;
